import { loadDeps, makeLighting, releaseContext } from './meshScene';
import { tallyRenderer } from './gpuTally';
import { makeExtentDims } from './meshAnnotations';

// THE MESH PHOTOGRAPHED DOWN ITS OWN SIX AXES, IN THE BROWSER.
//
// WHY IT MOVED HERE. This ran on the server: a cold Blender, a 300,000-triangle glTF import,
// six Workbench renders, six PNGs base64'd back — eleven and a half seconds, measured, for
// pictures of a mesh that was ALREADY DECODED AND ON SCREEN a few pixels away. In the
// browser the same six views are a few hundred milliseconds, and they are the pictures
// somebody just looked at rather than another program's idea of them.
//
// WHAT IT COSTS to move it: the server no longer builds everything it sends. The prompt
// still does — the words, and the order they name the pictures in — but the images now come
// from the page. That is a real property given up, and it is given up for a 30x on the half
// of the wait that was not the model.
//
// ITS OWN SCENE, AND NOT THE VIEWER'S, which is the part that would be tempting and wrong.
// The viewer's copy of this mesh can be MOVED AND TURNED by hand — that is what the coords
// panel is for — and six views shot through it would be six views of wherever somebody left
// the part. These names mean something only in the file's own frame, so the file is loaded
// again, at identity, and photographed there. It costs a parse and no download: the .glb is
// in the browser cache, which is where the viewer got it.
//
// THE FRAME. glTF is Y-up and three loads it unchanged, so the scene's axes ARE the file's:
// x across, y into the picture, z up — the frame meshAnnotations.FACES names, and the frame
// mesh_orient.VIEW_ORDER is written in. Nothing is rotated anywhere in here.

// WHICH SIDE EACH PICTURE IS OF, and in which order — mesh_orient.VIEW_ORDER on this side of
// the wire, which the server checks the names against when they arrive. The prompt numbers
// its pictures by this, so re-ordering it silently re-labels every picture the model sees.
//
// `at` is the axis the camera stands off, in the file's axes: the front is seen from −y, the
// top from +z. OFF and not ON — every shot is turned a little away from its axis, see YAW_DEG
// — but the picture is still of that side, and it is that side the answer names. `up` is which
// way is up in the picture: the part's own top for the four side views, and its back for the
// two that look along it, so the six read as one set.
const VIEWS = [
  { name: 'front', at: [0, -1, 0], up: [0, 0, 1] },
  { name: 'back', at: [0, 1, 0], up: [0, 0, 1] },
  { name: 'left', at: [-1, 0, 0], up: [0, 0, 1] },
  { name: 'right', at: [1, 0, 0], up: [0, 0, 1] },
  { name: 'top', at: [0, 0, 1], up: [0, 1, 0] },
  { name: 'bottom', at: [0, 0, -1], up: [0, 1, 0] },
] as const;

// AND HOW FAR OFF THAT AXIS THE CAMERA STANDS. Not on it, which is the whole of what these
// two numbers change.
//
// A SQUARE-ON ORTHOGRAPHIC VIEW OF A CHAIR IS A SILHOUETTE. Every depth cue projects onto one
// line: the seat's overhang, the third leg behind the two in front, the way the stretcher
// bows. Front and back of the stool this was written for are the same outline, and the model
// was picking between them by chance — which is the only mistake this feature makes that
// matters, since a half-turn about the standing axis swaps left and right along with them,
// and no answer given from any other pair of sides survives it either.
//
// Step off the axis and the depth separates: three legs stop being two, the seat plainly
// overhangs one end. It is the difference between the two pictures anybody would take of a
// chair to show somebody which way it faces.
//
// A YAW ABOUT THE VIEW'S OWN UP AND A RAISE ABOUT ITS OWN RIGHT, in that order, so it works
// for all six. The up of every view here is perpendicular to the direction it looks along, so
// a turn about it always walks the camera round the object — including for the two that look
// down the standing axis, where a turn about the direction ITSELF would only spin the picture
// in its frame and show nothing new.
//
// THEY COMPOSE, which is the thing to hold on to before touching either: the total offset
// from the axis is arccos(cos yaw · cos raise), so 15 and 8 is a shot 17° off — not 15, and
// not 23.
//
// THEY WERE 25 AND 15, WHICH IS 29° OFF, AND THAT WAS TOO MUCH — because the same angle does
// not cost every view the same. Seen from the SIDE, a part of length L is foreshortened by
// 1 − cos θ: 12% at 29°, which nobody can see. Seen down its OWN LENGTH, the same tilt drags
// L · sin θ across the picture: 48% of the whole part at 29°. On a chair that is nothing,
// every view being about as deep as it is wide — and the chair is what these numbers were
// chosen for. On a 70 mm screw it meant the two end-on views showed 34 mm of shaft against an
// 8 mm head, which is not a view down a screw at all but a three-quarter view of one, and it
// is what the answer for those two views was being read off.
//
// At 17° the same screw shows 20 mm, and the four side views lose 4% of their length. The
// cost is on the side the tilt was bought for — a chair's front and back separate a little
// less strongly than they did — and that is the trade, taken deliberately.
//
// MODEST ON PURPOSE. Past about a third of a right angle a view stops being of one side and
// becomes a mix of two, which is exactly the objection that took the 45° photographs out of
// the product shots — and here it would be worse, because the answer names the side the view
// is OF. The prompt says which one that is, and says these two numbers: mesh_orient._prompt
// tells the model the views are about 15° round and 8° above their axes, so changing them
// here means changing them there.
// EXPORTED, because a caller that shows these to a model has to be able to SAY what they
// are: Phase 5 reads a part's axis off the front and top views, and a picture turned by an
// amount nobody mentioned is a picture that quietly disagrees with the frame it is about.
// See buildFittingContext, which prints these two numbers into its prompt.
export const YAW_DEG = 15;
export const RAISE_DEG = 8;

// Room round the object rather than a tight crop: what is being judged is a shape against a
// photograph, and a mesh pressed against the edges of its frame reads as a different
// proportion from the same mesh with air round it.
const FRAME = 1.25;
// AND MORE OF IT WHEN THE BOX IS DRAWN, because the numbers are written outside the object:
// each sits at the middle of an edge and runs off it, so a crop that suits the mesh cuts the
// measurement in half. See `boxed`.
const BOXED_FRAME = 1.6;

// HOW BIG THE THREE NUMBERS ARE HERE, against the size the viewer draws them at. The share in
// meshAnnotations is chosen for a screen somebody leans into — at 512 px it comes out around
// ten pixels tall, which is a label a model reads by guessing. Passed as `textSpan`, which is
// exactly the knob that exists for it: what the words are sized against, rather than a second
// copy of the share.
const LABEL_ZOOM = 2.4;

// WHAT THE MEASURED BOX IS FOR, and it is one caller's — see buildFittingContext.
//
// A part is described to Phase 5 in words: three extents named by axis, or, for anything with
// a thread, its designation alone. "M6 x 70 mm" says what a screw IS and says nothing about
// which way it lies in its file, and that is the one thing these pictures exist to settle —
// so on the call that has to place the part, the words come back as a shape: twelve edges
// coloured by axis and three of them carrying their length in millimetres.
//
// THE SIX FACE PLATES STAY OFF, deliberately. They sit ON the object, which is the outline
// being read, and they name directions in a second vocabulary ("front", "left") that the
// answer would have to be translated out of. The coloured edges are already written in the
// axes the poses are answered in.
export type AxisBoxWords = { width: string; depth: string; height: string };
export type AxisBox = {
  // WHETHER THE THREE LENGTHS ARE WRITTEN ON IT. Off, the twelve coloured edges alone: Find
  // holes has every face's size in its text already, and on a thin rail three labels covered
  // the very wood a hole is being placed on. Written unless said otherwise.
  labels?: boolean;
  // MILLIMETRES PER FILE UNIT, from the caller's own `mmScale` — the mesh is loaded raw here,
  // and a box labelled in file units would print "depth: 0 mm" for a screw modelled in metres.
  mmPerUnit: number;
  words: AxisBoxWords;
};

// The six names the plates would carry, which they do not: `makeExtentDims` asks for them and
// this caller switches the plates off, so there is nothing to say. Blank rather than the real
// words because a word passed in and not drawn is a claim nobody can see to check.
const BLANK_FACES = {
  top: '', bottom: '', front: '', back: '', left: '', right: '',
};

export type AxisView = { name: string; dataUrl: string };

// The six, in VIEWS' order, as data URLs — and how long they took, which is the number the
// badge shows beside the model's own seconds.
// `only` narrows the set to the named views, in VIEWS' own order — Phase 5 wants two of the
// six (a part seen from the front and from above is enough to say which way it lies) and
// rendering four more would be four more WebGL frames per part, per call, thrown away.
//
// `frontUp` draws the TOP and BOTTOM views with the product's front at the top of the picture
// instead of its back — the convention partFaces states for those two faces' `v`, so a caller
// asking for a place on them (Find holes) shows pictures that read the way its rulers run.
// Off by default: the orientation call's prompt describes the pictures as they are.
export async function renderAxisViews(
  url: string, px = 512, only?: readonly string[], boxed?: AxisBox, frontUp = false,
): Promise<{ views: AxisView[]; seconds: number }> {
  const began = performance.now();
  const { THREE, GLTFLoader, RoomEnvironment } = await loadDeps();

  // NOT IN THE DOCUMENT. A canvas nobody adds is still a canvas WebGL will draw into, and
  // adding one would be a viewport appearing under the modal for a third of a second.
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  tallyRenderer(renderer);
  renderer.setPixelRatio(1);
  renderer.setSize(px, px, false);
  // WHITE, and opaque. The same white the server's renders were flattened onto and the same
  // white the listing photographs are shot on — what the model compares them against.
  renderer.setClearColor(0xffffff, 1);
  // THE VIEWER'S OWN EXPOSURE, and not three's defaults — which is most of what "what this
  // part looks like" means here: the wood's value, how dark a face reads. A second renderer
  // left on the defaults would send the model a lighter, flatter part than anybody in this
  // app has ever seen, and the whole call is a comparison of appearances. The curve and the
  // exposure both arrive with the lighting below (see makeLighting), so this renderer cannot
  // drift from the one on screen without the screen moving too.
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  // The viewer's own studio lighting, from the viewer's own builder. These pictures are
  // evidence about a surface — which face is dark, where the grain runs — so they have to be
  // lit the way the surface was judged. A second lighting here would be a second answer to
  // "what does this part look like".
  const lighting = makeLighting(THREE, renderer, scene, RoomEnvironment);
  lighting.apply('studio');

  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 100);
  const shots: AxisView[] = [];
  let root: import('three').Object3D | null = null;
  // Out here with `root` for the same reason: it holds a geometry and up to nine canvases, and
  // the finally below is what gives them back whichever way this exits.
  let dims: ReturnType<typeof makeExtentDims> | null = null;
  try {
    const gltf = await new GLTFLoader().loadAsync(url);
    root = gltf.scene;
    // INTO MILLIMETRES BEFORE ANYTHING IS MEASURED, where a box was asked for: the numbers it
    // writes are the numbers of the box it is given, so the scaling has to happen upstream of
    // the Box3 and not be applied to the labels afterwards. Uniform, so no picture changes —
    // the camera is fitted to whatever it finds.
    if (boxed) root.scale.setScalar(boxed.mmPerUnit);
    scene.add(root);

    const box = new THREE.Box3().setFromObject(root);
    const mid = box.getCenter(new THREE.Vector3());
    const radius = box.getBoundingSphere(new THREE.Sphere()).radius || 1;
    // Far enough out that nothing clips whichever way the part is longest, and the near
    // plane set from the same distance rather than left at a default that would cut into it.
    // The ball round the part rather than its longest side, because these views are corners
    // now: a cube one unit across measures 1.41 through its diagonal.
    const away = radius * 3;
    camera.near = away - radius * 1.5;
    camera.far = away + radius * 2;

    // THE BOX ITSELF, drawn round the mesh at the size it just measured. Built from the same
    // maker the viewer uses (see makeExtentDims): twelve edges coloured by axis, three of them
    // labelled with their length, and the six face plates switched off — which is also what
    // leaves the mesh's own outline uncovered.
    dims = boxed ? makeExtentDims(THREE, boxed.words, BLANK_FACES) : null;
    if (dims) {
      const span = box.getSize(new THREE.Vector3());
      scene.add(dims.group);
      dims.showFaces(false);
      if (boxed?.labels === false) {
        dims.show(false);
        dims.showBox(true);
      }
      dims.update(box, Math.max(span.x, span.y, span.z) * LABEL_ZOOM);
    }

    // WHERE THE CAMERA STANDS FOR EACH, worked out before any of them is drawn — because the
    // frame they share is a function of all six. Off the axis in the view's own frame: round
    // the object about its up, then up over its own shoulder. `right` is taken AFTER the yaw
    // so the raise is a raise in the picture that is about to be made rather than in the one
    // that would have been.
    const yaw = (YAW_DEG * Math.PI) / 180;
    const raise = (RAISE_DEG * Math.PI) / 180;
    const wanted = only ? VIEWS.filter((v) => only.includes(v.name)) : VIEWS;
    const stands = wanted.map((view) => {
      const flat = view.name === 'top' || view.name === 'bottom';
      const up = frontUp && flat
        ? new THREE.Vector3(0, -1, 0)
        : new THREE.Vector3(view.up[0], view.up[1], view.up[2]);
      const dir = new THREE.Vector3(view.at[0], view.at[1], view.at[2])
        .applyAxisAngle(up, yaw);
      dir.applyAxisAngle(new THREE.Vector3().crossVectors(up, dir).normalize(), raise);
      return { view, up, at: mid.clone().addScaledVector(dir, away) };
    });

    // Aim the camera as this view will be shot and put it there — the two passes below both
    // need it, and the corners are measured through the same matrix that will draw them.
    const aim = (stand: typeof stands[number]) => {
      camera.position.copy(stand.at);
      // The part's own up, still — three levels the shot against it, so what tilts is the
      // object and not the horizon. A picture of a chair leaning is a picture of a chair;
      // one where the floor leans is a picture of a camera.
      camera.up.copy(stand.up);
      camera.lookAt(mid);
      camera.updateMatrixWorld(true);
    };

    // ONE FRAME FOR ALL SIX, and it is the widest of the six that decides it. The pictures
    // have to be at ONE SCALE: a part that is long in one and stubby in the next is a
    // proportion the model would have to discount, and the proportions are most of what it is
    // matching. Fitted each to its own frame, every picture would fill its square and the set
    // would say nothing about the shape.
    //
    // MEASURED RATHER THAN GUESSED AT, which is what the tilt costs. Square-on, the widest a
    // box can look is its longest side; from a corner it is some combination of two of them,
    // and there is no expression for it worth trusting over eight points through the matrix
    // that is about to draw them.
    const corners: import('three').Vector3[] = [];
    for (const x of [box.min.x, box.max.x]) {
      for (const y of [box.min.y, box.max.y]) {
        for (const z of [box.min.z, box.max.z]) corners.push(new THREE.Vector3(x, y, z));
      }
    }
    const seen = new THREE.Vector3();
    let half = 0;
    for (const stand of stands) {
      aim(stand);
      for (const corner of corners) {
        seen.copy(corner).applyMatrix4(camera.matrixWorldInverse);
        half = Math.max(half, Math.abs(seen.x), Math.abs(seen.y));
      }
    }
    half = (half || radius) * (boxed ? BOXED_FRAME : FRAME);
    camera.left = -half;
    camera.right = half;
    camera.top = half;
    camera.bottom = -half;
    camera.updateProjectionMatrix();

    for (const stand of stands) {
      aim(stand);
      renderer.render(scene, camera);
      shots.push({ name: stand.view.name, dataUrl: renderer.domElement.toDataURL('image/png') });
    }
  } finally {
    // Everything the loader built, walked from the objects themselves — a GLB brings however
    // many geometries and textures it feels like, and counting on one of each is how a page
    // leaks a mesh per press.
    root?.traverse((o) => {
      const m = o as import('three').Mesh;
      m.geometry?.dispose?.();
      const mat = m.material;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else (mat as import('three').Material | undefined)?.dispose?.();
    });
    scene.environment = null;
    dims?.dispose();
    lighting.dispose();
    // AND THE CONTEXT ITSELF. This one is built and dropped on every press and every hover
    // of the badge beside it, so a renderer that only disposed its buffers here was the
    // fastest way in the app to spend the browser's handful of contexts. See releaseContext.
    releaseContext(renderer);
  }
  return { views: shots, seconds: Math.round((performance.now() - began) / 100) / 10 };
}
